import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert as InlineAlert, Box, Button, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Alert, ErrorAlert } from "../../utils";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { password as passwordValidations, passwordBackendErrorMessages } from "../../utils/form/validations";
import { Passwords as PasswordFields } from "../utils/user/form";
import * as loginGatewayApi from "../../apis/loginGatewayApi";
import { isSecuritySignatureExpired } from "./securitySignature";

const defaultErrorMessage = "No pudimos cambiar tu contraseña. Intentá de nuevo.";
const expiredLinkMessage = "El link para cambiar tu contraseña venció. Pedí uno nuevo.";

const isExpiredLinkError = (error) => error?.response?.status === 401;

const formSchema = yup.object().shape(passwordValidations)

const ChangePassword = () => {
  const form = useForm({
    defaultValues: formSchema.cast(),
    resolver: yupResolver(formSchema)
  });
  const [ loginStatus, setLoginStatus ] = useState(null);
  const [ error, setError ] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleSubmit } = form;

  const securitySignature = searchParams.get("securitySignature");
  const [ expiredOnArrival ] = useState(() => !!securitySignature && isSecuritySignatureExpired(securitySignature));

  useEffect(() => {
    if (!securitySignature)
      return navigate('/login');
  }, []);

  const requestPasswordRecovery = ({ password }) => {
    setLoginStatus("loading");
    loginGatewayApi.changePassword({ password, securitySignature })
    .then(() => {
      setLoginStatus("success")
      setTimeout(() => {
        navigate('/login')
      }, 3500);
    })
    .catch((error) => {
      setError(error);
      setLoginStatus("error");
    });
  };

  const onAlertClose = () => {
    setLoginStatus(null);
    setError(null);
  };

  const isLoading = loginStatus === "loading";
  const expiredLink = expiredOnArrival || (loginStatus === "error" && isExpiredLinkError(error));
  const failed = loginStatus === "error" && !expiredLink;
  const succeeded = loginStatus === "success";
  const errorMessage = passwordBackendErrorMessages(error).join(". ") || defaultErrorMessage;

  return (
    <Stack display="flex" flexDirection="column" alignItems="center" spacing="2rem" pt="2rem">
      <Box>
        Cambiá tu contraseña
      </Box>
      {expiredLink ? (
        <Stack alignItems="center" pt="3rem" px="1rem" spacing="2rem" width="100%" maxWidth="500px" boxSizing="border-box">
          <InlineAlert severity="warning" sx={{ width: "100%", boxSizing: "border-box" }}>
            {expiredLinkMessage}
          </InlineAlert>
          <Button variant="contained" sx={{ paddingX: "6rem" }} onClick={() => navigate("/passwordRecovery/request")}>
            Pedir link nuevo
          </Button>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(requestPasswordRecovery)} autoComplete="off" style={{ width: "100%", maxWidth: "500px" }}>
          <Stack display="flex" flexDirection="column" alignItems="center" pt="3rem" px="1rem" spacing="2rem">
            <PasswordFields form={form} label="Nueva contraseña" />
            <LoadingButton type="submit" variant="contained" sx={{paddingX: "6rem"}} loading={isLoading} disabled={isLoading || succeeded}>
              Cambiar contraseña
            </LoadingButton>
          </Stack>
        </form>
      )}
      <ErrorAlert
        show={failed}
        onClose={onAlertClose}
        message={errorMessage}
      />
      <Alert
        severity="success"
        message="Listo! Cambiaste tu contraseña. Te redirigimos al inicio de sesión..."
        show={succeeded}
      />
    </Stack>
  );
};

export default ChangePassword;
